import { createSdk } from '@commerce-blocks/sdk';

export function html(strings, ...values) {
  return strings.reduce((result, str, i) => {
    const value = values[i - 1] !== undefined ? values[i - 1] : '';
    return result + value + str;
  });
}

const LAYERS_TOKEN_PLACEHOLDER = 'YOUR_LAYERS_PUBLIC_TOKEN';
const SDK_STATE = {
  sdk: null,
  error: null,
};

const DEFAULT_PAGE_SIZE = 24;
const SEARCH_UI = {
  resultsWrapper: '[data-search-results-wrapper]',
  resultsCount: '.collection__products-count',
  searchForm: '[data-search-form]',
  filtersForm: '[data-collection-filters-form]',
};

const COLLECTION_UI = {
  resultsCount: '.collection__products-count',
  filtersForm: '[data-collection-filters-form]',
  filtersList: '[data-collection-filters-list]',
  sortOptionsList: '.collection__sort__options .collection__filter-tags',
  sortBarOptionsList: '.collection__sort-bar .collection__filter-tags',
};

const PAGINATION_UI = {
  container: '[data-layers-pagination]',
};

function getSectionContainer(element) {
  if (!element) return null;
  return (
    element.closest('[data-section-type]') ||
    element.closest('.shopify-section') ||
    element.closest('section')
  );
}

function getLayersConfig() {
  return window.layersConfig || {};
}

function getPublicToken() {
  return getLayersConfig().publicToken || LAYERS_TOKEN_PLACEHOLDER;
}

function normalizeId(value) {
  if (value == null) return '';
  const raw = String(value).trim();
  if (!raw) return '';
  if (!raw.includes('gid://')) return raw;
  const parts = raw.split('/');
  return parts[parts.length - 1] || raw;
}

function normalizeSorts(items) {
  if (!Array.isArray(items)) return [];
  const normalized = items
    .map((item) => ({
      code: item.code,
      label: item.name,
      scope: item.scope,
      order: item.order,
    }))
    .filter((item) => item.code && item.label);
  return normalized.sort((a, b) => {
    const aOrder = Number.isFinite(Number(a.order)) ? Number(a.order) : Number.POSITIVE_INFINITY;
    const bOrder = Number.isFinite(Number(b.order)) ? Number(b.order) : Number.POSITIVE_INFINITY;
    if (aOrder !== bOrder) return aOrder - bOrder;
    return String(a.label).localeCompare(String(b.label));
  });
}

function normalizeFacets(items) {
  if (!Array.isArray(items)) return [];
  return items.map((item) => item.code).filter(Boolean);
}

function getSdk() {
  if (SDK_STATE.sdk || SDK_STATE.error) return SDK_STATE.sdk;
  const token = getPublicToken();
  if (!token || token === LAYERS_TOKEN_PLACEHOLDER) {
    console.warn('[Layers] Public token placeholder detected. Update window.layersConfig.publicToken.');
  }

  const config = getLayersConfig();
  const sorts = normalizeSorts(config.sortOrders || config.sorts || []);
  const facets = normalizeFacets(config.facets || []);
  const sortMap = config.sortMap || {};

  const result = createSdk({
    layersPublicToken: token,
    sorts,
    facets,
    sortMap,
  });

  if (result.error) {
    SDK_STATE.error = result.error;
    console.error('[Layers] SDK init failed', result.error.message);
    return null;
  }

  SDK_STATE.sdk = result.data;
  return SDK_STATE.sdk;
}

function getUrlParams() {
  return new URLSearchParams(window.location.search);
}

function parseFiltersFromUrl() {
  const params = getUrlParams();
  const listFilters = {};
  const rangeFilters = {};
  let hasListFilters = false;
  let hasRangeFilters = false;
  params.forEach((value, key) => {
    if (value === '') return;
    const rangeMatch = key.match(/^filters\[(.+)\]\[(min|max)\]$/);
    if (rangeMatch) {
      const facetKey = rangeMatch[1];
      const bound = rangeMatch[2];
      if (!facetKey) return;
      const numeric = Number(value);
      if (!Number.isFinite(numeric)) return;
      if (!rangeFilters[facetKey]) rangeFilters[facetKey] = {};
      rangeFilters[facetKey][bound] = numeric;
      hasRangeFilters = true;
      return;
    }

    const match = key.match(/^filters\[(.+)\]$/);
    if (!match) return;
    const facetKey = match[1];
    if (!facetKey) return;
    const normalizedValue = stripWrappingQuotes(value);
    if (!normalizedValue) return;
    hasListFilters = true;
    if (listFilters[facetKey]) {
      listFilters[facetKey] = Array.isArray(listFilters[facetKey])
        ? [...listFilters[facetKey], normalizedValue]
        : [listFilters[facetKey], normalizedValue];
    } else {
      listFilters[facetKey] = normalizedValue;
    }
  });

  return {
    listFilters: hasListFilters ? listFilters : undefined,
    rangeFilters: hasRangeFilters ? rangeFilters : undefined,
  };
}

function sanitizeFilterParams(params) {
  if (!params) return false;
  let changed = false;
  const toDelete = [];
  params.forEach((value, key) => {
    if (!key.startsWith('filters[')) return;
    if (value === '') toDelete.push(key);
  });
  if (!toDelete.length) return false;
  toDelete.forEach((key) => {
    params.delete(key);
    changed = true;
  });
  return changed;
}

function collectFilterInputs(form) {
  if (!form) return [];
  const inputs = Array.from(form.querySelectorAll('input[name^="filters["]'));
  if (inputs.length) return inputs;
  const section = form.closest('[data-section-type], .shopify-section, section');
  if (!section) return inputs;
  return Array.from(section.querySelectorAll('input[name^="filters["]'));
}

function parseFiltersFromForm(form) {
  if (!form) {
    return { listFilters: undefined, rangeFilters: undefined };
  }
  const listFilters = {};
  const rangeFilters = {};
  let hasListFilters = false;
  let hasRangeFilters = false;

  const inputs = collectFilterInputs(form);
  if (window?.theme?.settings?.layersDebug) {
    const sample = inputs.slice(0, 12).map((input) => ({
      name: input.name,
      value: input.value,
      checked: Boolean(input.checked),
      disabled: Boolean(input.disabled),
      type: input.type,
    }));
    console.log('[Layers][Filters] form inputs', { count: inputs.length, sample });
  }
  inputs.forEach((input) => {
    if (!input || !input.name || input.disabled) return;
    const key = input.name;
    let value = String(input.value ?? '').trim();
    const rangeMatch = key.match(/^filters\[(.+)\]\[(min|max)\]$/);
    if (rangeMatch) {
      if (value === '') return;
      const facetKey = rangeMatch[1];
      const bound = rangeMatch[2];
      if (!facetKey) return;
      const numeric = Number(value);
      if (!Number.isFinite(numeric)) return;
      if (!rangeFilters[facetKey]) rangeFilters[facetKey] = {};
      rangeFilters[facetKey][bound] = numeric;
      hasRangeFilters = true;
      return;
    }

    const match = key.match(/^filters\[(.+)\]$/);
    if (!match) return;
    if (input.type === 'checkbox' || input.type === 'radio') {
      if (!input.checked) return;
    } else if (value === '') {
      return;
    }
    value = stripWrappingQuotes(value);
    if (value === '') return;
    const facetKey = match[1];
    if (!facetKey) return;
    hasListFilters = true;
    if (listFilters[facetKey]) {
      listFilters[facetKey] = Array.isArray(listFilters[facetKey])
        ? [...listFilters[facetKey], value]
        : [listFilters[facetKey], value];
    } else {
      listFilters[facetKey] = value;
    }
  });

  return {
    listFilters: hasListFilters ? listFilters : undefined,
    rangeFilters: hasRangeFilters ? rangeFilters : undefined,
  };
}

function buildFilterGroup(listFilters, rangeFilters) {
  if (!listFilters && !rangeFilters) return undefined;

  const config = getLayersConfig();
  const filterMap = config.filterMap || {};
  const expressions = [];

  if (listFilters) {
    Object.entries(listFilters).forEach(([key, rawValue]) => {
      if (rawValue == null) return;
      const mapEntry = filterMap[key];
      const property = typeof mapEntry === 'string' ? mapEntry : mapEntry?.property || key;
      const valueMap = typeof mapEntry === 'object' ? mapEntry.values : undefined;
      const values = (Array.isArray(rawValue) ? rawValue : [rawValue]).map((value) => {
        const normalized = String(value);
        return valueMap?.[normalized] ?? normalized;
      });
      if (!values.length) return;
      expressions.push({
        property,
        operator: values.length > 1 ? 'in' : 'eq',
        values,
      });
    });
  }

  if (rangeFilters) {
    Object.entries(rangeFilters).forEach(([key, range]) => {
      if (!range) return;
      const mapEntry = filterMap[key];
      const property = typeof mapEntry === 'string' ? mapEntry : mapEntry?.property || key;
      const min = Number(range.min);
      const max = Number(range.max);
      if (Number.isFinite(min)) {
        expressions.push({ property, operator: 'gte', values: [min] });
      }
      if (Number.isFinite(max)) {
        expressions.push({ property, operator: 'lte', values: [max] });
      }
    });
  }

  if (!expressions.length) return undefined;
  return { conditional: 'AND', expressions };
}

function buildFiltersFromUrl() {
  const { listFilters, rangeFilters } = parseFiltersFromUrl();
  const group = buildFilterGroup(listFilters, rangeFilters);
  if (window?.theme?.settings?.layersDebug) {
    console.log('[Layers][Filters] url parsed', { listFilters, rangeFilters, group });
  }
  return group;
}

function buildFiltersFromForm(form) {
  const { listFilters, rangeFilters } = parseFiltersFromForm(form);
  const group = buildFilterGroup(listFilters, rangeFilters);
  if (window?.theme?.settings?.layersDebug) {
    console.log('[Layers][Filters] form parsed', { listFilters, rangeFilters, group });
  }
  return group;
}

function getSortSelection() {
  const config = getLayersConfig();
  const params = getUrlParams();
  const sortBy = params.get('sort') || params.get('sort_by') || undefined;
  if (!sortBy) return undefined;
  if (config.sortMap && config.sortMap[sortBy]) return { code: config.sortMap[sortBy] };
  return { code: sortBy };
}

function getDefaultSortCode(container) {
  const section = getSectionContainer(container) || container;
  const component = section?.querySelector?.('layers-collection-results, layers-search-results');
  const componentDefault = component?.dataset?.defaultSort;
  if (componentDefault) return componentDefault;
  const config = getLayersConfig();
  return config.defaultSortOrder || undefined;
}

function isNonDefaultSortApplied(container) {
  const selection = getSortSelection();
  if (!selection?.code) return false;
  const defaultSort = getDefaultSortCode(container);
  if (!defaultSort) return true;
  return selection.code !== defaultSort;
}

function isSortAccordionTrigger(trigger) {
  if (!trigger) return false;
  if (trigger.classList?.contains('collection__sort__label')) return true;
  if (trigger.closest?.('.collection__sort')) return true;
  const controls = trigger.getAttribute?.('aria-controls') || '';
  return Boolean(controls && /sorting/i.test(controls));
}

function isSortInput(target) {
  if (!target || typeof target.matches !== 'function') return false;
  return target.matches(
    [
      'input[name="sort"]',
      'input[name="sort_by"]',
      'input[name="sort_by_bar"]',
      'select[name="sort"]',
      'select[name="sort_by"]',
      'select[name="sort_by_bar"]',
      '[data-input-sort]',
    ].join(', ')
  );
}

function getSortInputValue(target) {
  if (!target) return '';
  if (target.tagName && target.tagName.toLowerCase() === 'select') {
    return target.value || '';
  }
  if (typeof target.value !== 'undefined') return target.value || '';
  return target.getAttribute?.('data-sort') || '';
}

function filterSortsByScope(sorts, scopeTokens) {
  const tokens = Array.isArray(scopeTokens) ? scopeTokens : [scopeTokens];
  const normalizedTokens = tokens.filter(Boolean).map((token) => String(token).toLowerCase());
  if (!normalizedTokens.length) return sorts;
  return sorts.filter((item) => {
    if (!Array.isArray(item.scope)) return false;
    const scopeSet = item.scope.map((value) => String(value).toLowerCase());
    return normalizedTokens.some((token) => scopeSet.includes(token));
  });
}

function updateSortOptions(container, scopeTokens) {
  if (!container) return;
  const config = getLayersConfig();
  const sorts = filterSortsByScope(
    normalizeSorts(config.sortOrders || config.sorts || []),
    scopeTokens
  );
  const sortBars = container.querySelectorAll('.collection__sort-bar, .collection__sort');
  if (!sorts.length) {
    sortBars.forEach((el) => el.setAttribute('hidden', 'true'));
    return;
  }
  sortBars.forEach((el) => el.removeAttribute('hidden'));

  const current = getSortSelection()?.code || config.defaultSortOrder || sorts[0].code;
  const lists = container.querySelectorAll(`${COLLECTION_UI.sortOptionsList}, ${COLLECTION_UI.sortBarOptionsList}`);
  lists.forEach((list) => {
    list.innerHTML = '';
    sorts.forEach((sort) => {
      const li = document.createElement('li');
      li.className = 'collection__filter-tag';
      const id = `layers-sort-${sort.code}-${Math.random().toString(36).slice(2, 7)}`;
      const inputName = 'sort';
      li.innerHTML = html`
        <input class="collection__sort__input" type="radio" name="${inputName}" value="${sort.code}" id="${id}" ${sort.code === current ? 'checked' : ''}>
        <label class="collection__sort__button" for="${id}" tabindex="0">${sort.label}</label>
      `;
      list.appendChild(li);
    });
  });
}

function handleize(value) {
  return String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function stripWrappingQuotes(value) {
  const normalized = String(value ?? '').trim();
  if (!normalized) return '';
  return normalized.replace(/^["']+|["']+$/g, '');
}

function hasActiveFilterParams(params = getUrlParams()) {
  let active = false;
  params.forEach((value, key) => {
    if (!key.startsWith('filters[')) return;
    const normalized = stripWrappingQuotes(value);
    if (normalized !== '') active = true;
  });
  return active;
}

function mergeFacetBuckets(base, incoming) {
  if (!base && !incoming) return {};
  if (!base) return { ...(incoming || {}) };
  if (!incoming) return { ...(base || {}) };
  const merged = { ...base };
  Object.entries(incoming).forEach(([facetKey, values]) => {
    if (!values || typeof values !== 'object') {
      merged[facetKey] = values;
      return;
    }
    const existing = merged[facetKey];
    if (!existing || typeof existing !== 'object') {
      merged[facetKey] = values;
      return;
    }
    merged[facetKey] = { ...existing, ...values };
  });
  return merged;
}

function mergeFacetRanges(base, incoming) {
  if (!base && !incoming) return {};
  if (!base) return { ...(incoming || {}) };
  if (!incoming) return { ...(base || {}) };
  return { ...base, ...incoming };
}

function getClearFiltersLabel(section) {
  const button = section?.querySelector('.collection__filters__reset');
  const label = button?.textContent?.trim();
  return label || 'Clear filters';
}

function updateClearFiltersButton(section, hasActiveFilters) {
  const buttons = section?.querySelectorAll('.collection__filters__reset') || [];
  if (!buttons.length) return;
  buttons.forEach((button) => {
    const forceVisible = button.hasAttribute('data-clear-always-visible');
    if (hasActiveFilters || forceVisible) {
      button.classList.add('is-visible');
      button.removeAttribute('hidden');
    } else {
      button.classList.remove('is-visible');
      button.setAttribute('hidden', '');
    }

    if (forceVisible) {
      const shouldDisable = !hasActiveFilters;
      button.toggleAttribute('disabled', shouldDisable);
      button.classList.toggle('is-disabled', shouldDisable);
      button.setAttribute('aria-disabled', shouldDisable ? 'true' : 'false');
    } else {
      button.removeAttribute('disabled');
      button.classList.remove('is-disabled');
      button.removeAttribute('aria-disabled');
    }
  });
}

let filtersFormIdCounter = 0;
const FILTER_TOGGLE_ICONS = `
  <svg aria-hidden="true" focusable="false" role="presentation" class="icon icon-toggle-plus" viewBox="0 0 192 192"><path d="M30 96h132M96 30v132" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/></svg>
  <svg aria-hidden="true" focusable="false" role="presentation" class="icon icon-toggle-minus" viewBox="0 0 192 192"><path d="M30 96h132" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/></svg>
`;
const accordionStateBySection = new Map();

function getFilterToggleIcons() {
  return FILTER_TOGGLE_ICONS;
}

function getAccordionSectionKey(container) {
  const section = getSectionContainer(container) || container;
  return (
    section?.dataset?.sectionId ||
    section?.dataset?.section ||
    section?.getAttribute?.('data-section-id') ||
    section?.id ||
    'global'
  );
}

function getAccordionStateMap(container) {
  const key = getAccordionSectionKey(container);
  if (!accordionStateBySection.has(key)) {
    accordionStateBySection.set(key, new Map());
  }
  return accordionStateBySection.get(key);
}

function getAccordionStateKey({ key, label, controls, fallback }) {
  return normalizeFacetText(key || label || controls || fallback);
}

function captureAccordionState(container) {
  if (!container) return;
  const map = getAccordionStateMap(container);
  const triggers = container.querySelectorAll(
    `${FILTER_TRIGGER_SELECTOR}:not(${SHOW_MORE_TRIGGER_SELECTOR})`
  );
  triggers.forEach((trigger) => {
    const stateKey = getAccordionStateKey({
      key: trigger.getAttribute('data-filter-key'),
      label: trigger.getAttribute('data-filter-label'),
      controls: trigger.getAttribute('aria-controls'),
      fallback: trigger.textContent,
    });
    if (!stateKey) return;
    map.set(stateKey, trigger.classList.contains('is-expanded'));
  });
}

function getStoredAccordionState(container, options) {
  const map = getAccordionStateMap(container);
  const stateKey = getAccordionStateKey(options);
  if (!stateKey || !map.has(stateKey)) return null;
  return map.get(stateKey);
}

function resolveAccordionState(container, { key, label, controls, fallback, hasSwatches }) {
  const stored = getStoredAccordionState(container, { key, label, controls, fallback });
  if (stored !== null && stored !== undefined) return stored;
  return Boolean(hasSwatches || isColorFacet(label, key));
}

function resolveCollapsibleElements(trigger) {
  if (!trigger) return null;
  const controlsId = trigger.getAttribute('aria-controls');
  if (!controlsId) return null;
  const root =
    trigger.closest('[data-collection-filters]') ||
    trigger.closest('[data-collection-filters-list]') ||
    trigger.closest('[data-section-type]') ||
    trigger.closest('.shopify-section') ||
    document;
  const selector = `#${CSS?.escape ? CSS.escape(controlsId) : controlsId}`;
  const container = root.querySelector(selector) || document.getElementById(controlsId);
  if (!container) return null;
  const content = container.querySelector('[data-collapsible-content]');
  if (!content) return null;
  return { container, content };
}

function setCollapsibleState(trigger, shouldExpand) {
  const elements = resolveCollapsibleElements(trigger);
  if (!elements) return;
  const { container, content } = elements;
  const isExpanded = trigger.classList.contains('is-expanded');
  if (shouldExpand === isExpanded) return;

  const expandedValue = shouldExpand ? 'true' : 'false';
  trigger.classList.toggle('is-expanded', shouldExpand);
  trigger.setAttribute('aria-expanded', expandedValue);
  container.classList.toggle('is-expanded', shouldExpand);
  container.setAttribute('aria-expanded', expandedValue);

  if (shouldExpand) {
    const height = content.offsetHeight;
    container.style.height = `${height}px`;
    window.setTimeout(() => {
      container.style.height = 'auto';
    }, 500);
  } else {
    const height = content.offsetHeight;
    container.style.height = `${height}px`;
    window.requestAnimationFrame(() => {
      container.style.height = '0px';
    });
    window.setTimeout(() => {
      container.style.height = '';
    }, 500);
  }
}

function initLayersCollapsibles(container) {
  if (!container) return;
  const root = container.querySelector('[data-collection-filters]') || container;
  if (root.dataset.layersCollapsibleDelegate === 'true') return;
  root.dataset.layersCollapsibleDelegate = 'true';

  root.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-collapsible-trigger]');
    if (!trigger || !root.contains(trigger)) return;
    if (trigger.matches(SHOW_MORE_TRIGGER_SELECTOR)) return;
    event.preventDefault();
    const shouldExpand = !trigger.classList.contains('is-expanded');
    setCollapsibleState(trigger, shouldExpand);
  });

  root.addEventListener('keyup', (event) => {
    const trigger = event.target.closest('[data-collapsible-trigger]');
    if (!trigger || !root.contains(trigger)) return;
    if (trigger.matches(SHOW_MORE_TRIGGER_SELECTOR)) return;
    if (event.code !== 'Space' && event.code !== 'Enter' && event.code !== 'NumpadEnter') return;
    event.preventDefault();
    const shouldExpand = !trigger.classList.contains('is-expanded');
    setCollapsibleState(trigger, shouldExpand);
  });
}

function ensureFilterModalButtons(section) {
  if (!section) return;
  const modal = section.querySelector('[data-collection-filters]');
  if (!modal) return;
  const filtersList = modal.querySelector('.collection__filters-list');
  const filtersForm = modal.querySelector('[data-collection-filters-form]');
  if (!filtersForm) return;
  if (!filtersForm.id) {
    filtersFormIdCounter += 1;
    filtersForm.id = `layers-filters-form-${Date.now()}-${filtersFormIdCounter}`;
  }
  let bottom = modal.querySelector('.collection__filters-bottom');

  if (!bottom && filtersList) {
    bottom = document.createElement('div');
    bottom.className = 'collection__filters-bottom';
    filtersList.appendChild(bottom);
  }

  if (!bottom) return;

  bottom.classList.add('is-visible');

  let buttonsWrap = bottom.querySelector('.collection__filters__buttons');
  let closeButton = bottom.querySelector('.collection__filters__close-bottom');

  if (!buttonsWrap) {
    buttonsWrap = document.createElement('div');
    buttonsWrap.className = 'collection__filters__buttons';
    if (closeButton) {
      buttonsWrap.appendChild(closeButton);
    }
    bottom.appendChild(buttonsWrap);
  }

  if (!closeButton) {
    closeButton = document.createElement('button');
    closeButton.className = 'collection__filters__close-bottom btn btn--primary';
    buttonsWrap.appendChild(closeButton);
  }
  closeButton.type = 'submit';
  closeButton.setAttribute('data-close-filters', '');
  closeButton.setAttribute('form', filtersForm.id);
  closeButton.textContent = 'Apply';

  let clearButton = buttonsWrap.querySelector('.collection__filters__reset');
  if (!clearButton) {
    clearButton = document.createElement('button');
    clearButton.type = 'button';
    clearButton.className = 'collection__filters__reset btn btn--secondary';
    clearButton.setAttribute('data-filter-reset-button', '');
    clearButton.setAttribute('data-layers-clear-filters', '');
    buttonsWrap.insertBefore(clearButton, closeButton);
  }
  clearButton.setAttribute('data-clear-always-visible', '');
  clearButton.textContent = getClearFiltersLabel(section);

  updateClearFiltersButton(section, hasActiveFilterParams());
}

function updateFilterLabels(container) {
  if (!container) return;
  const root = getSectionContainer(container) || container;
  const modalLabel = 'FILTER & SORT';
  const toggleLabelText = window.innerWidth < MOBILE_BREAKPOINT ? modalLabel : 'Filter';
  const title = root.querySelector('.collection__filters__title span');
  if (title) title.textContent = modalLabel;
  const toggleLabel = root.querySelector('.collection__filters__toggle span');
  if (toggleLabel) toggleLabel.textContent = toggleLabelText;
}

function buildNoResultsNode({ message, clearLabel, showClear }) {
  const wrapper = document.createElement('div');
  wrapper.className = 'collection--no-results';
  wrapper.setAttribute('data-layers-no-results', '');

  const paragraph = document.createElement('p');
  paragraph.className = 'no-matches';
  paragraph.textContent = message;
  wrapper.appendChild(paragraph);

  if (showClear) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'collection__filters__reset is-visible';
    button.setAttribute('data-layers-clear-filters', '');
    button.textContent = clearLabel || 'Clear filters';
    wrapper.appendChild(button);
  }

  return wrapper;
}

function setNoResultsState(section, hasResults) {
  if (!section) return;
  section.classList.toggle('collection--no-results', !hasResults);
}

function getCurrencySymbol() {
  const format = window.theme?.moneyFormat || '';
  const symbol = format.replace(/\{\{.*?\}\}/g, '').trim();
  return symbol || '$';
}

function getRangeSelection(facetKey) {
  const params = getUrlParams();
  const minValue = params.get(`filters[${facetKey}][min]`);
  const maxValue = params.get(`filters[${facetKey}][max]`);
  const min = minValue !== null && minValue !== '' ? Number(minValue) : undefined;
  const max = maxValue !== null && maxValue !== '' ? Number(maxValue) : undefined;
  return {
    min: Number.isFinite(min) ? min : undefined,
    max: Number.isFinite(max) ? max : undefined,
  };
}

function isPriceFacetKey(facetKey) {
  if (!facetKey) return false;
  return facetKey === 'variants.price';
}

function isColorFacetKey(facetKey, label) {
  const normalizedLabel = String(label || '').trim().toLowerCase();
  const normalizedKey = String(facetKey || '').trim().toLowerCase();
  if (
    normalizedLabel === 'color' ||
    normalizedLabel === 'colour' ||
    normalizedLabel.includes('color') ||
    normalizedLabel.includes('colour')
  ) {
    return true;
  }
  return (
    normalizedKey === 'color' ||
    normalizedKey.endsWith('.color') ||
    normalizedKey.endsWith('_color') ||
    normalizedKey.includes('color')
  );
}

function parseFacetRange(range) {
  if (!range || typeof range !== 'object') return null;
  const minRaw =
    range.min ?? range.min_value ?? range.minValue ?? range.from ?? range.low ?? range.start;
  const maxRaw =
    range.max ?? range.max_value ?? range.maxValue ?? range.to ?? range.high ?? range.end;
  const min = Number(minRaw?.amount ?? minRaw);
  const max = Number(maxRaw?.amount ?? maxRaw);
  if (!Number.isFinite(min) || !Number.isFinite(max)) return null;
  return { min, max };
}

function resolveFacetRange(facetKey, facetRanges) {
  if (!facetRanges) return null;
  const direct = parseFacetRange(facetRanges[facetKey]);
  if (direct) return direct;
  if (!facetKey || !/price/i.test(facetKey)) return null;
  const priceKey = Object.keys(facetRanges).find((key) => /price/i.test(key));
  if (!priceKey) return null;
  return parseFacetRange(facetRanges[priceKey]);
}

function updateFiltersUI(container, facetsData, facetRanges) {
  if (!container) return;
  const filtersList = container.querySelector(COLLECTION_UI.filtersList);
  if (!filtersList) return;

  const COLOR_SWATCH_MAP = {
    black: '#000000',
    white: '#FFFFFF',
    red: '#D32F2F',
    blue: '#1976D2',
    green: '#388E3C',
    yellow: '#FBC02D',
    orange: '#F57C00',
    purple: '#7B1FA2',
    pink: '#C2185B',
    brown: '#6D4C41',
    gray: '#757575',
    grey: '#757575',
    multi: null,
  };
  const swatchStyle =
    window.theme?.settings?.colorSwatchesProductStyle ||
    window.theme?.settings?.color_swatches_product_style ||
    'circle';

  const facets = getLayersConfig().facets || [];
  const facetLabelMap = facets.reduce((acc, facet) => {
    if (facet && facet.code) acc[facet.code] = facet.name || facet.code;
    return acc;
  }, {});
  captureAccordionState(container);

  const orderedKeys = [];
  facets.forEach((facet) => {
    if (facet?.code) orderedKeys.push(facet.code);
  });
  Object.keys(facetsData || {}).forEach((key) => {
    if (!orderedKeys.includes(key)) orderedKeys.push(key);
  });
  Object.keys(facetRanges || {}).forEach((key) => {
    if (!orderedKeys.includes(key)) orderedKeys.push(key);
  });

  const colorKeys = [];
  const otherKeys = [];
  orderedKeys.forEach((key) => {
    const title = facetLabelMap[key] || key;
    if (isColorFacetKey(key, title)) {
      colorKeys.push(key);
    } else {
      otherKeys.push(key);
    }
  });
  const orderedFacetKeys = [...otherKeys, ...colorKeys];

  const selections = new Map();
  getUrlParams().forEach((value, key) => {
    const match = key.match(/^filters\[(.+)\]$/);
    if (!match) return;
    const facetKey = match[1];
    const normalizedValue = stripWrappingQuotes(value);
    if (!normalizedValue) return;
    if (!selections.has(facetKey)) selections.set(facetKey, new Set());
    selections.get(facetKey).add(normalizedValue);
  });

  filtersList.innerHTML = '';
  if (!orderedKeys.length) return;

  orderedFacetKeys.forEach((facetKey) => {
    const range = resolveFacetRange(facetKey, facetRanges);
    if (range && range.min !== range.max) {
      const title = facetLabelMap[facetKey] || facetKey;
      const rangeMin = Math.floor(range.min);
      const rangeMax = Math.ceil(range.max);
      const selection = getRangeSelection(facetKey);
      const hasMinSelection = Number.isFinite(selection.min);
      const hasMaxSelection = Number.isFinite(selection.max);
      const selectedMin = hasMinSelection ? Math.max(rangeMin, Math.floor(selection.min)) : rangeMin;
      const selectedMax = hasMaxSelection ? Math.min(rangeMax, Math.ceil(selection.max)) : rangeMax;
      const sliderMin = Math.min(selectedMin, selectedMax);
      const sliderMax = Math.max(selectedMin, selectedMax);
      const inputMinValue = hasMinSelection ? sliderMin : '';
      const inputMaxValue = hasMaxSelection ? sliderMax : '';
      const minPlaceholder = sliderMin;
      const maxPlaceholder = hasMaxSelection && sliderMax !== rangeMax ? sliderMax : rangeMax;
      const groupId = `CollectionFilter--${handleize(title) || handleize(facetKey) || 'price'}`;
      const shouldExpand = resolveAccordionState(container, {
        key: facetKey,
        label: title,
        controls: groupId,
        fallback: title,
        hasSwatches: false,
      });
      const expandedClass = shouldExpand ? ' is-expanded' : '';
      const expandedAttr = shouldExpand ? 'true' : 'false';
      const expandedStyle = shouldExpand ? 'style="height:auto;"' : '';
      const currencySymbol = getCurrencySymbol();

      const group = document.createElement('div');
      group.className = 'collection__filter';
      group.innerHTML = html`
        <button type="button" class="collection__filter__title label-typography${expandedClass}" data-collapsible-trigger aria-expanded="${expandedAttr}" aria-controls="${groupId}" data-filter-label="${title}" data-filter-key="${facetKey}">
          <span>${title}</span>
          <span class="collection__filter__count">${getFilterToggleIcons()}</span>
        </button>
        <div id="${groupId}" class="collection__filter-tags-wrapper${expandedClass}" data-collapsible-container aria-expanded="${expandedAttr}" ${expandedStyle}>
          <ul class="collection__filter-tags collection__filter-tags--default" data-collapsible-content>
            <li class="collection__filter-tag collection__filter-tag--default">
              <div class="filter__price" data-range-holder>
                <div class="filter__price__range range" data-range-slider data-range-filter-update
                  data-se-min="${rangeMin}"
                  data-se-step="1"
                  data-se-min-value="${sliderMin}"
                  data-se-max-value="${sliderMax}"
                  data-se-max="${rangeMax}">
                  <div class="range__dot range__dot--left" data-range-left><span>&nbsp;</span></div>
                  <div class="range__dot range__dot--right" data-range-right><span>&nbsp;</span></div>
                  <div class="range__line"><span data-range-line>&nbsp;</span></div>
                </div>
                <div class="filter__price__fields">
                  <div class="filter__price__field filter__price__from">
                    <span>${currencySymbol}</span>
                    <input
                      data-field-price-min
                      class="filter__price__input"
                      name="filters[${facetKey}][min]"
                      id="filter-price-${handleize(facetKey)}-min"
                      value="${inputMinValue}"
                      type="number"
                      placeholder="${minPlaceholder}"
                      min="${rangeMin}"
                      max="${rangeMax}">
                    <label for="filter-price-${handleize(facetKey)}-min">From</label>
                  </div>
                  <div class="filter__price__field filter__price__to">
                    <span>${currencySymbol}</span>
                    <input
                      data-field-price-max
                      class="filter__price__input"
                      name="filters[${facetKey}][max]"
                      id="filter-price-${handleize(facetKey)}-max"
                      value="${inputMaxValue}"
                      type="number"
                      placeholder="${maxPlaceholder}"
                      min="${rangeMin}"
                      max="${rangeMax}">
                    <label for="filter-price-${handleize(facetKey)}-max">To</label>
                  </div>
                </div>
              </div>
            </li>
          </ul>
        </div>
      `;

      filtersList.appendChild(group);
      return;
    }

    if (isPriceFacetKey(facetKey)) return;

    const values = facetsData?.[facetKey];
    const selectionSet = selections.get(facetKey);
    const entries = Object.entries(values || {}).filter(([value, count]) => {
      const normalizedValue = stripWrappingQuotes(value);
      if (!normalizedValue) return false;
      return Number(count) > 0 || (selectionSet && selectionSet.has(normalizedValue));
    });
    if (entries.length <= 1) return;

    const title = facetLabelMap[facetKey] || facetKey;
    const normalizedTitle = String(title || '').trim().toLowerCase();
    const normalizedKey = String(facetKey || '').trim().toLowerCase();
    const isColorFilter =
      normalizedTitle === 'color' ||
      normalizedKey === 'color' ||
      normalizedKey.endsWith('.color') ||
      normalizedKey.endsWith('_color') ||
      normalizedKey.includes('color');
    const isSizeFilter =
      normalizedTitle === 'size' ||
      normalizedTitle === 'sizes' ||
      normalizedKey === 'size' ||
      normalizedKey.endsWith('.size') ||
      normalizedKey.endsWith('_size') ||
      normalizedKey.includes('size');
    const listGroupId = `CollectionFilter--${handleize(title) || handleize(facetKey) || 'filter'}`;
    const shouldExpand = resolveAccordionState(container, {
      key: facetKey,
      label: title,
      controls: listGroupId,
      fallback: title,
      hasSwatches: isColorFilter,
    });
    const expandedClass = shouldExpand ? ' is-expanded' : '';
    const expandedAttr = shouldExpand ? 'true' : 'false';
    const expandedStyle = shouldExpand ? 'style="height:auto;"' : '';

    const group = document.createElement('div');
    group.className = `collection__filter${isSizeFilter ? ' collection__filter--size' : ''}`;

    group.innerHTML = html`
      <button type="button" class="collection__filter__title label-typography${expandedClass}" data-collapsible-trigger aria-expanded="${expandedAttr}" aria-controls="${listGroupId}" data-filter-label="${title}" data-filter-key="${facetKey}">
        <span>${title}</span>
        <span class="collection__filter__count">${getFilterToggleIcons()}</span>
      </button>
      <div id="${listGroupId}" class="collection__filter-tags-wrapper${expandedClass}" data-collapsible-container aria-expanded="${expandedAttr}" ${expandedStyle}>
        <ul class="collection__filter-tags${isColorFilter ? ` collection__filter-tags--swatches collection__filter-tags--swatches-${swatchStyle}` : ' collection__filter-tags--default'}" data-collapsible-content></ul>
      </div>
    `;

    const ul = group.querySelector('ul');
    entries.forEach(([rawValue, count], index) => {
      const value = stripWrappingQuotes(rawValue);
      if (!value) return;
      const li = document.createElement('li');
      const id = `layers-filter-${handleize(facetKey)}-${handleize(value)}-${index}`;
      const checked = selectionSet?.has(value) ? 'checked' : '';
      const disabled = Number(count) <= 0 ? 'disabled' : '';
      if (isColorFilter) {
        const displayValue = stripWrappingQuotes(value);
        const normalizedValue = displayValue.toLowerCase();
        const hex = COLOR_SWATCH_MAP[normalizedValue] ?? null;
        const swatchColor = hex || '#e0e0e0';
        li.className = 'collection__filter-tag collection__filter-tag--swatch';

        const wrapper = document.createElement('span');
        wrapper.className = `swatch__button swatch__button--${swatchStyle} filter__button`;

        const input = document.createElement('input');
        input.type = 'checkbox';
        input.className = 'filter__input swatch__input';
        input.name = `filters[${facetKey}]`;
        input.value = displayValue;
        input.id = id;
        if (checked) input.checked = true;
        if (disabled) input.disabled = true;

        const label = document.createElement('label');
        label.className = `swatch__label swatch__label--${swatchStyle} filter__button`;
        label.setAttribute('for', id);
        label.dataset.swatch = normalizedValue;
        label.dataset.tooltip = displayValue;
        label.dataset.hex = hex || '';
        label.style.setProperty('--swatch', swatchColor);
        label.style.background = swatchColor;

        const hidden = document.createElement('span');
        hidden.className = 'visually-hidden';
        hidden.textContent = displayValue;

        const icon = document.createElement('span');
        icon.className = 'icon icon-check';

        label.append(hidden, icon);
        wrapper.append(input, label);
        li.appendChild(wrapper);
      } else {
        const displayValue = stripWrappingQuotes(value);
        li.className = 'collection__filter-tag collection__filter-tag--default';

        const input = document.createElement('input');
        input.type = 'checkbox';
        input.className = 'filter__input';
        input.name = `filters[${facetKey}]`;
        input.value = displayValue;
        input.id = id;
        if (checked) input.checked = true;
        if (disabled) input.disabled = true;

        const label = document.createElement('label');
        label.className = 'filter__button';
        label.setAttribute('for', id);
        label.textContent = displayValue;

        li.append(input, label);
      }
      ul.appendChild(li);
    });

    filtersList.appendChild(group);
  });

  updateFilterLabels(container);
  initLayersCollapsibles(container);
  ensureFilterModalButtons(container);
  document.dispatchEvent(new CustomEvent('theme:filters:init', { bubbles: true }));
}

function getPageFromUrl() {
  const page = Number(getUrlParams().get('page'));
  return Number.isFinite(page) && page > 0 ? page : 1;
}

function getSearchQueryFromUrl() {
  return getUrlParams().get('q') || '';
}

function getSearchIdFromUrl() {
  return getUrlParams().get('search_id') || '';
}

function buildSearchUrl(query, searchId) {
  const base = window.theme?.routes?.searchUrl || '/search';
  const params = new URLSearchParams();
  params.set('q', query);
  params.set('type', 'product');
  if (searchId) params.set('search_id', searchId);
  return `${base}?${params.toString()}`;
}

function getTotalResults(data, fallback = 0) {
  const total = data?.total ?? data?.totalResults ?? data?.total_results;
  return Number.isFinite(Number(total)) ? Number(total) : fallback;
}

function getTotalPages(data, limit, fallback = 1) {
  const totalPages = data?.totalPages ?? data?.total_pages;
  if (Number.isFinite(Number(totalPages))) return Number(totalPages);
  const totalResults = getTotalResults(data);
  if (Number.isFinite(Number(totalResults)) && Number.isFinite(Number(limit)) && Number(limit) > 0) {
    return Math.max(1, Math.ceil(Number(totalResults) / Number(limit)));
  }
  return fallback;
}

function buildPageUrl(page) {
  const params = getUrlParams();
  if (Number(page) > 1) {
    params.set('page', String(page));
  } else {
    params.delete('page');
  }
  const query = params.toString();
  return `${window.location.pathname}${query ? `?${query}` : ''}`;
}

function buildPaginationItems(currentPage, totalPages) {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  let start = Math.max(2, currentPage - 2);
  let end = Math.min(totalPages - 1, currentPage + 2);

  if (currentPage <= 3) {
    start = 2;
    end = Math.min(totalPages - 1, 5);
  }

  if (currentPage >= totalPages - 2) {
    end = totalPages - 1;
    start = Math.max(2, totalPages - 4);
  }

  const items = [1];
  if (start > 2) items.push('ellipsis');
  for (let page = start; page <= end; page += 1) {
    items.push(page);
  }
  if (end < totalPages - 1) items.push('ellipsis');
  items.push(totalPages);

  return items;
}

function renderPagination(container, { currentPage, totalPages }) {
  if (!container) return;
  if (!Number.isFinite(Number(totalPages)) || totalPages <= 1) {
    container.innerHTML = '';
    container.setAttribute('hidden', '');
    return;
  }

  const safeCurrent = Math.min(
    Math.max(1, Number(currentPage) || 1),
    Number(totalPages)
  );
  container.removeAttribute('hidden');
  container.classList.add('pagination');
  container.setAttribute('data-pagination', '');

  const items = buildPaginationItems(safeCurrent, totalPages);
  const markup = [];

  if (safeCurrent > 1) {
    markup.push(
      html`<span class="prev"><a href="${buildPageUrl(safeCurrent - 1)}" data-page="${safeCurrent - 1}">&laquo; Previous</a></span>`
    );
  }

  items.forEach((item) => {
    if (item === 'ellipsis') {
      markup.push(html`<span class="page">…</span>`);
      return;
    }
    if (item === safeCurrent) {
      markup.push(html`<span class="page current">${item}</span>`);
      return;
    }
    markup.push(
      html`<span class="page"><a href="${buildPageUrl(item)}" data-page="${item}">${item}</a></span>`
    );
  });

  if (safeCurrent < totalPages) {
    markup.push(
      html`<span class="next"><a href="${buildPageUrl(safeCurrent + 1)}" data-page="${safeCurrent + 1}">Next &raquo;</a></span>`
    );
  }

  container.innerHTML = markup.join('');
}

function getPaginationContainer(component) {
  if (!component) return null;
  let container = component.querySelector(PAGINATION_UI.container);
  if (!container) {
    container = component.parentElement?.querySelector(PAGINATION_UI.container) || null;
  }
  if (!container) {
    const section = getSectionContainer(component);
    container = section?.querySelector(PAGINATION_UI.container) || null;
  }
  if (!container) {
    container = document.createElement('div');
    container.className = 'pagination';
    container.setAttribute('data-layers-pagination', '');
    component.appendChild(container);
  }
  return container;
}

function getGridClasses({ columns = 4, columnsMobile = 2, index = 0, carousel = false } = {}) {
  const desktop = columns === 1 ? 'one-whole'
    : columns === 2 ? 'one-half'
      : columns === 3 ? 'one-third'
        : 'one-quarter';
  const mobile = carousel ? 'mobile--one-whole' : (columnsMobile === 1 ? 'mobile--one-whole' : 'mobile--one-half');

  const mobileCounter = index + 1;
  const tabletCounter = index + 1;
  const counterEven = mobileCounter % 2;
  const counterSecondTablet = (tabletCounter + 1) % 3;
  const counterThirdTablet = tabletCounter % 3;

  let gridItemClass = '';
  if (counterEven === 0) gridItemClass += ' grid-item--even';
  if (columns > 2) {
    if (counterSecondTablet === 0) gridItemClass += ' grid-item--second-tablet';
    else if (counterThirdTablet === 0) gridItemClass += ' grid-item--third-tablet';
  }

  const base = carousel ? 'carousel__item' : 'grid__item';
  return `${base} ${desktop} ${mobile}${gridItemClass}`;
}

function formatPriceRange(product) {
  const min = product.priceRange?.min?.formatted || '';
  const max = product.priceRange?.max?.formatted || '';
  if (min && max && min !== max) {
    return `From ${min}`;
  }
  return min || max || '';
}

function splitTitleColor(title) {
  if (!title) return { title: '', color: '' };
  const spacedIndex = title.lastIndexOf(' - ');
  if (spacedIndex > 0 && spacedIndex < title.length - 3) {
    return {
      title: title.slice(0, spacedIndex).trim(),
      color: title.slice(spacedIndex + 3).trim(),
    };
  }

  return { title: title.trim(), color: '' };
}

function getReviewSummary(product) {
  const reviews = product?.metafields?.reviews || product?.metafields?.rating || null;
  if (!reviews) return null;
  let rating = null;
  let count = null;

  const ratingField = reviews.rating ?? reviews.rating_value ?? reviews.value ?? reviews.average ?? reviews.score;
  if (ratingField != null) {
    if (typeof ratingField === 'object') {
      if (ratingField.value && typeof ratingField.value === 'object') {
        rating = ratingField.value.rating ?? ratingField.value.value ?? ratingField.value.average;
        count = ratingField.value.count ?? count;
      } else {
        rating = ratingField.rating ?? ratingField.value ?? ratingField.average ?? ratingField;
      }
    } else {
      rating = ratingField;
    }
  }

  if (count == null) {
    count = reviews.rating_count ?? reviews.count ?? reviews.reviews_count ?? reviews.total;
  }

  const numericRating = Number(rating);
  if (!Number.isFinite(numericRating) || numericRating <= 0) return null;
  const numericCount = Number(count);
  return {
    rating: Math.round(numericRating * 10) / 10,
    count: Number.isFinite(numericCount) && numericCount > 0 ? numericCount : null,
  };
}

function isOnSale(product) {
  const compare = product.compareAtPriceRange?.min?.amount;
  const price = product.priceRange?.min?.amount;
  return Number(compare) > Number(price);
}

function renderProductCard(product, { columns, columnsMobile, index = 0, aosDelay = 0, aosAnchor = '', carousel = false } = {}) {
  if (!product) return '';
  const classes = getGridClasses({ columns, columnsMobile, index, carousel });
  const url = product.onlineStoreUrl || `/products/${product.handle}`;
  const image = product.images?.[0];
  const hoverImage = product.images?.[1];
  const fullTitle = product.title || '';
  const { title, color } = splitTitleColor(fullTitle);
  const price = formatPriceRange(product);
  const onSale = isOnSale(product);
  const compare = product.compareAtPriceRange?.min?.formatted || '';
  const animationsEnabled = window.theme?.settings?.animations === true;
  const headingStyle = window.theme?.settings?.headingStyle || '';
  const contentAlignment = window.theme?.settings?.contentAlignment || 'left';
  const hoverMode = window.theme?.settings?.productGridHover;
  const hoverAnimation = window.theme?.settings?.productGridHoverAnimation || 'fade';
  const showSaleBadge = window.theme?.settings?.showSaleBadge;
  const showSoldBadge = window.theme?.settings?.showSoldBadge;
  const showCustomBadge = window.theme?.settings?.showCustomBadge;
  const showSavingBadge = window.theme?.settings?.showSavingBadge;
  const soldOut = product.availableForSale === false;
  const customBadge = product?.metafields?.theme?.badge?.value || product?.metafields?.theme?.badge;
  const savingBadgeText = getSavingBadgeText(product);
  const saleText = window.theme?.strings?.sale_badge_text || 'Sale';
  const soldOutText = window.theme?.strings?.sold_out || 'Sold out';
  const itemId = `product-item--layers-${product.id || product.handle}`;

  const ratingMarkup = ''

  const badgeItems = [];
  if (customBadge && showCustomBadge) {
    badgeItems.push(html`<div class="product__badge__item product__badge__item--custom"><span>${customBadge}</span></div>`);
  }
  if (soldOut && showSoldBadge) {
    badgeItems.push(html`<div class="product__badge__item product__badge__item--sold"><span>${soldOutText}</span></div>`);
  }
  if (onSale && showSaleBadge && !soldOut && !showSavingBadge) {
    badgeItems.push(html`<div class="product__badge__item product__badge__item--sale"><span>${saleText}</span></div>`);
  }
  if (showSavingBadge && savingBadgeText) {
    badgeItems.push(html`<div class="product__badge__item product__badge__item--saving"><span>${savingBadgeText}</span></div>`);
  }

  return html`
    <div class="product-grid-item ${classes}${soldOut ? ' soldout' : ''}" ${carousel ? 'data-slide' : ''} data-product-block id="${itemId}">
      <div class="product-grid-item__image" data-product-media-container>
        <a class="product__media__holder" href="${url}" aria-label="${title}" data-grid-link
          ${animationsEnabled ? `data-aos="zoom-out" data-aos-easing="ease" data-aos-duration="1000" ${aosAnchor ? `data-aos-anchor="${aosAnchor}"` : ''} data-aos-delay="${aosDelay}"` : ''}>
          <div class="product__media__image ${hoverMode === 'image' && hoverImage ? `product__media__image--hover-${hoverAnimation}` : 'product__media__image--hover-none'}" data-product-media-featured>
            ${image?.url ? `<img class="product__media product__media--featured-visible" src="${image.url}" alt="${image.alt || title}" loading="lazy" />` : ''}
            <span class="visually-hidden">${image?.alt || title}</span>
          </div>
          ${hoverMode === 'image' && hoverImage ? `
            <div class="product__media__hover product__media__hover--${hoverAnimation}">
              <img class="product__media__hover-img product__media product__media__hover-img--visible" src="${hoverImage.url}" alt="${hoverImage.alt || title}" loading="lazy" />
            </div>` : ''}
          ${hoverMode === 'slideshow' && product.images?.length > 1 ? `
            <div class="product__media__hover product__media__hover--${hoverAnimation} product__media__slider" data-product-media-slideshow>
              ${product.images.slice(1, 5).map((img) => `
                <div class="product__media__slide" data-product-media-slideshow-slide>
                  <img class="product__media" src="${img.url}" alt="${img.alt || title}" loading="lazy" />
                </div>`).join('')}
            </div>
            ${product.images.length > 2 ? `
              <div class="progress-bar">
                <div class="progress-bar__inner" data-product-slideshow-progress></div>
              </div>` : ''}` : ''}
        </a>
      </div>

      <div class="product-grid-item__info product-grid-item__info--${contentAlignment}">
        <div class="product-grid-item__info-content">
          <div class="product-grid-item__title-row">
            <a class="product-grid-item__title ${headingStyle}" href="${url}" aria-label="${fullTitle}" data-grid-link>
              ${title || fullTitle}
            </a>
            ${ratingMarkup}
          </div>
          ${color ? html`<div class="product-grid-item__color">${color}</div>` : ''}
          <a class="product-grid-item__price price" href="${url}" data-grid-link>
            <div class="price-wrapper" style="margin-top: 5px;" data-product-handle="${product.handle}">
              ${onSale ? html`<span class="product-grid-item__price__new">${price}</span><s>${compare}</s>` : price}
            </div>
          </a>
        </div>
      </div>
      ${badgeItems.length ? html`
        <div class="product__badge" data-product-badge ${animationsEnabled ? `data-aos="fade" ${aosAnchor ? `data-aos-anchor="${aosAnchor}"` : ''} data-aos-delay="${aosDelay}"` : ''}>
          ${badgeItems.join('')}
        </div>` : ''}
    </div>
  `;
}

function getSavingBadgeText(product) {
  const showSavingBadge = window.theme?.settings?.showSavingBadge;
  if (!showSavingBadge) return '';
  const price = Number(product.priceRange?.min?.amount || 0);
  const compare = Number(product.compareAtPriceRange?.min?.amount || 0);
  if (!compare || compare <= price) return '';
  const difference = compare - price;
  const savingType = window.theme?.settings?.savingBadgeType || 'amount';
  if (savingType === 'percentage') {
    const percent = Math.floor((difference / compare) * 100);
    const template = product.variants?.length > 1
      ? (window.theme?.strings?.saving_up_to_badge || 'Save up to {{discount}}')
      : (window.theme?.strings?.saving_badge || 'Save {{discount}}');
    return template.replace('{{discount}}', `${percent}%`);
  }
  const currency = product.priceRange?.min?.currencyCode || 'USD';
  const formatted = new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(difference);
  const template = product.variants?.length > 1
    ? (window.theme?.strings?.saving_up_to_badge || 'Save up to {{discount}}')
    : (window.theme?.strings?.saving_badge || 'Save {{discount}}');
  return template.replace('{{discount}}', formatted);
}

function setResultsCount(container, count) {
  if (!container) return;
  const countEl = container.querySelector(COLLECTION_UI.resultsCount);
  if (countEl) {
    const numeric = Number(count);
    if (Number.isFinite(numeric)) {
      countEl.textContent = `${numeric} Products`;
    } else if (count !== null && count !== undefined && count !== '') {
      countEl.textContent = `${count} Products`;
    } else {
      countEl.textContent = '';
    }
  }
}

function updateSearchTitle(container, count) {
  if (!container) return;
  const titleEl = container.querySelector('[data-search-title]') || container.querySelector('.search-page__title');
  if (!titleEl) return;
  const fallback = titleEl.getAttribute('data-search-title-fallback') || titleEl.textContent || '';
  const template = titleEl.getAttribute('data-search-title-template') || 'Results for %%terms%%';
  const query = getSearchQueryFromUrl() || titleEl.getAttribute('data-search-terms') || '';
  if (!query) {
    titleEl.textContent = fallback;
    return;
  }
  const base = template.replace('%%terms%%', query);
  const numeric = Number(count);
  if (Number.isFinite(numeric)) {
    titleEl.textContent = `${numeric} ${base}`;
  } else {
    titleEl.textContent = base;
  }
}

function extractPromoPlan(container) {
  if (!container) return [];
  const promoContainer = container.querySelector('[data-layers-promos]');
  if (!promoContainer) return [];
  const promos = [];
  Array.from(promoContainer.children).forEach((child) => {
    const index = Number(child.getAttribute('data-layers-promo-index'));
    if (!Number.isFinite(index)) return;
    promos.push({
      index,
      html: child.outerHTML,
    });
  });

  return promos;
}

function injectPromos(cards, promos) {
  if (!promos || promos.length === 0) return cards;
  const next = [...cards];
  promos.forEach((promo, offset) => {
    const insertAt = Math.min(promo.index + offset, next.length);
    next.splice(insertAt, 0, promo.html);
  });
  return next;
}

class LayersCollectionResults extends HTMLElement {
  connectedCallback() {
    this.grid = this.querySelector('[data-layers-grid]');
    if (!this.grid) return;
    const section = getSectionContainer(this);
    this.handle = this.dataset.collectionHandle;
    this.limit = Number(this.dataset.limit) || DEFAULT_PAGE_SIZE;
    this.columns = Number(this.dataset.columns) || 4;
    this.columnsMobile = Number(this.dataset.columnsMobile) || 2;
    this.sectionAnchor = this.grid.id ? `#${this.grid.id}` : '';
    this.promos = extractPromoPlan(section);
    this.isLoading = false;
    this.carousel = this.dataset.carousel === 'true' || this.dataset.carousel === '1';
    this.page = getPageFromUrl();
    this.totalPages = 1;
    this.paginationContainer = this.dataset.pagination === 'false' ? null : getPaginationContainer(this);
    this.skeletonMarkup = this.grid.innerHTML;
    this.hasSkeleton = this.skeletonMarkup.trim().length > 0;
    this.filtersList = section?.querySelector(COLLECTION_UI.filtersList) || null;
    this.filtersSkeletonMarkup = this.filtersList?.innerHTML || '';
    this.hasFiltersSkeleton = this.filtersSkeletonMarkup.trim().length > 0;
    this.activeFilters = null;
    this.facetSnapshot = null;
    this.facetRangeSnapshot = null;
    this.lazyLoad = this.dataset.lazyLoad === 'true' || this.dataset.lazyLoad === '1';
    this.lazyRootMargin = this.dataset.lazyRootMargin || '200px';
    this.refreshTimer = null;
    this.bindListeners();
    this.blankFiltersAndSort();
    updateSortOptions(section, ['collection', 'browse']);
    if (this.lazyLoad) {
      this.setupLazyLoad();
    } else {
      this.refresh();
    }

    const params = getUrlParams();
    if (sanitizeFilterParams(params)) {
      history.replaceState({ layers: true }, '', `${window.location.pathname}?${params.toString()}`);
    }
  }

  scrollToGridTop() {
    if (!this.grid) return;
    const offset = Number(window.stickyHeaderHeight) || 0;
    const top = this.grid.getBoundingClientRect().top + window.pageYOffset - offset - 16;
    window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
  }

  showSkeleton() {
    if (!this.grid) return;
    this.teardownCarousel();
    if (!this.hasSkeleton) {
      this.grid.innerHTML = '';
      return;
    }
    this.grid.innerHTML = this.skeletonMarkup;
  }

  teardownCarousel() {
    if (!this.carousel || !this.grid) return;
    const Flickity = window.themeVendor?.Flickity || window.Flickity;
    const flkty = Flickity?.data ? Flickity.data(this.grid) : null;
    if (flkty) {
      flkty.destroy();
    }
    this.grid.classList.remove('flickity-enabled');
    const viewport = this.grid.querySelector('.flickity-viewport');
    if (viewport) {
      const slider = viewport.querySelector('.flickity-slider');
      if (slider) {
        while (slider.firstChild) {
          this.grid.appendChild(slider.firstChild);
        }
      }
      viewport.remove();
    }
    this.grid.style.removeProperty('height');
  }

  refreshCarousel() {
    if (!this.carousel || !this.grid) return;
    requestAnimationFrame(() => {
      if (this.grid.hasAttribute('data-slider')) {
        this.grid.dispatchEvent(new CustomEvent('theme:slider:resize', { bubbles: true }));
      }
      document.dispatchEvent(new CustomEvent('theme:resize:width', { bubbles: true }));
    });
  }

  scheduleRefresh(delay = 50) {
    if (this.refreshTimer) window.clearTimeout(this.refreshTimer);
    this.refreshTimer = window.setTimeout(() => {
      this.refreshTimer = null;
      this.refresh();
    }, delay);
  }

  interceptFiltersForm(form) {
    if (!form || form.dataset.layersIntercepted === 'true') return;
    this.onFiltersInputCapture = (event) => {
      event.stopImmediatePropagation();
    };
    this.onFiltersSubmitCapture = (event) => {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.activeFilters = buildFiltersFromForm(form);
      this.updateUrlFromFilters(form);
      this.scheduleRefresh(0);
    };
    form.addEventListener('input', this.onFiltersInputCapture, true);
    form.addEventListener('submit', this.onFiltersSubmitCapture, true);
    form.dataset.layersIntercepted = 'true';
  }

  setupLazyLoad() {
    if (this.lazyObserver) return;
    if (!('IntersectionObserver' in window)) {
      this.refresh();
      return;
    }
    this.lazyObserver = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.lazyObserver.disconnect();
        this.lazyObserver = null;
        this.refresh();
      },
      { rootMargin: this.lazyRootMargin, threshold: 0.01 }
    );
    this.lazyObserver.observe(this);
  }

  bindListeners() {
    this.onUrlChange = () => {
      this.activeFilters = null;
      this.refresh();
    };
    window.addEventListener('popstate', this.onUrlChange);

    const section = getSectionContainer(this);
    this.filtersResetButtons = Array.from(
      section?.querySelectorAll('.collection__filters__reset') || []
    );
    if (this.filtersResetButtons.length) {
      this.onFiltersResetClick = (event) => {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.clearFilters();
      };
      this.filtersResetButtons.forEach((button) => {
        button.addEventListener('click', this.onFiltersResetClick, { capture: true });
      });
    }
    this.onNoResultsClearClick = (event) => {
      const button = event.target.closest('[data-layers-clear-filters]');
      if (!button) return;
      if (button.hasAttribute('disabled') || button.getAttribute('aria-disabled') === 'true') return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.clearFilters();
    };
    this.grid?.addEventListener('click', this.onNoResultsClearClick);
    section?.addEventListener('click', this.onNoResultsClearClick);
    section?.addEventListener('click', this.onNoResultsClearClick);
    const filtersForm = section?.querySelector(COLLECTION_UI.filtersForm);
    if (filtersForm) {
      this.filtersForm = filtersForm;
      this.interceptFiltersForm(filtersForm);
      filtersForm.addEventListener('change', (event) => {
        if (isSortInput(event.target)) {
          event.preventDefault();
          event.stopImmediatePropagation();
          const sortValue = getSortInputValue(event.target);
          if (sortValue) {
            this.updateUrlFromSort(sortValue);
            this.scheduleRefresh();
          }
          return;
        }
        event.preventDefault();
        event.stopImmediatePropagation();
        this.activeFilters = buildFiltersFromForm(filtersForm);
        this.updateUrlFromFilters(filtersForm);
        this.scheduleRefresh();
      });
      filtersForm.addEventListener('theme:filter:range-update', (event) => {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.activeFilters = buildFiltersFromForm(filtersForm);
        this.updateUrlFromFilters(filtersForm);
        this.scheduleRefresh();
      });
    }
    section?.addEventListener('change', (event) => {
      if (!isSortInput(event.target)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      const sortValue = getSortInputValue(event.target);
      if (sortValue) {
        this.updateUrlFromSort(sortValue);
        setTimeout(this.refresh.bind(this), 50);
      }
    });

    this.paginationContainer?.addEventListener('click', (event) => {
      const link = event.target.closest('a[data-page]');
      if (!link) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      const page = Number(link.dataset.page);
      if (!Number.isFinite(page) || page < 1 || page === this.page) return;
      this.updateUrlFromPage(page);
      setTimeout(() => {
        this.fetchPage(page, { append: false }).then(() => this.scrollToGridTop());
      }, 50);
    });
  }

  disconnectedCallback() {
    window.removeEventListener('popstate', this.onUrlChange);
    this.lazyObserver?.disconnect();
    if (this.filtersForm && this.onFiltersInputCapture) {
      this.filtersForm.removeEventListener('input', this.onFiltersInputCapture, true);
    }
    if (this.filtersForm && this.onFiltersSubmitCapture) {
      this.filtersForm.removeEventListener('submit', this.onFiltersSubmitCapture, true);
    }
    if (this.filtersResetButtons && this.onFiltersResetClick) {
      this.filtersResetButtons.forEach((button) => {
        button.removeEventListener('click', this.onFiltersResetClick, { capture: true });
      });
    }
    if (this.grid && this.onNoResultsClearClick) {
      this.grid.removeEventListener('click', this.onNoResultsClearClick);
    }
  }

  clearFilters() {
    const params = getUrlParams();
    let changed = false;
    [...params.keys()].forEach((key) => {
      if (key.startsWith('filters[')) {
        params.delete(key);
        changed = true;
      }
    });
    params.delete('page');
    if (changed) {
      history.pushState({ layers: true }, '', `${window.location.pathname}?${params.toString()}`);
    }
    this.activeFilters = null;
    this.scheduleRefresh(0);
  }

  blankFiltersAndSort() {
    const container = getSectionContainer(this);
    if (!container) return;
    const lists = container.querySelectorAll(`${COLLECTION_UI.sortOptionsList}, ${COLLECTION_UI.sortBarOptionsList}, ${COLLECTION_UI.filtersList}`);
    lists.forEach((list) => {
      if (list.matches(COLLECTION_UI.filtersList) && this.hasFiltersSkeleton) {
        list.innerHTML = this.filtersSkeletonMarkup;
        return;
      }
      list.innerHTML = '';
    });
  }

  updateUrlFromSort(sortCode) {
    const params = getUrlParams();
    params.set('sort', sortCode);
    params.delete('page');
    history.pushState({ layers: true }, '', `${window.location.pathname}?${params.toString()}`);
  }

  updateUrlFromFilters(form) {
    const params = getUrlParams();
    [...params.keys()].forEach((key) => {
      if (key.startsWith('filters[')) params.delete(key);
    });
    const inputs = collectFilterInputs(form);
    inputs.forEach((input) => {
      if (!input || !input.name || input.disabled) return;
      const key = input.name;
      let value = String(input.value ?? '').trim();
      if (!key.startsWith('filters[')) return;
      if (input.type === 'checkbox' || input.type === 'radio') {
        if (!input.checked) return;
      }
      value = stripWrappingQuotes(value);
      if (value === '') return;
      params.append(key, value);
    });
    params.delete('page');
    sanitizeFilterParams(params);
    history.pushState({ layers: true }, '', `${window.location.pathname}?${params.toString()}`);
  }

  updateUrlFromPage(page) {
    const params = getUrlParams();
    if (Number(page) > 1) {
      params.set('page', String(page));
    } else {
      params.delete('page');
    }
    history.pushState({ layers: true }, '', `${window.location.pathname}?${params.toString()}`);
  }

  async fetchPage(page, { append = false } = {}) {
    const sdk = getSdk();
    if (!sdk || !this.handle) return;
    if (!append) this.showSkeleton();
    const section = getSectionContainer(this);
    const config = getLayersConfig();
    const baseSorts = normalizeSorts(config.sortOrders || config.sorts || []);
    const collectionSorts = filterSortsByScope(baseSorts, ['collection', 'browse']);

    const controller = sdk.collection({
      handle: this.handle,
      defaultSort: this.dataset.defaultSort,
    });
    const filters = this.activeFilters ?? buildFiltersFromUrl();
    const sortSelection = getSortSelection();
    const sortAllowed =
      !sortSelection ||
      !collectionSorts.length ||
      collectionSorts.some((item) => item.code === sortSelection.code);
    if (!sortAllowed && sortSelection) {
      console.warn('[Layers] Ignoring sort order outside collection scope.');
    }
    const defaultSort = this.dataset.defaultSort || getLayersConfig().defaultSortOrder;
    const sortOrderCode = sortAllowed
      ? sortSelection?.code || defaultSort || undefined
      : defaultSort || undefined;

    const result = await controller.execute({
      page,
      limit: this.limit,
      filters,
      sortOrderCode,
      includeFilters: true,
      includeFacetRanges: true,
    });

    if (window?.theme?.settings?.layersDebug) {
      console.log('[Layers][Collection] request', { page, limit: this.limit, filters, sortOrderCode });
    }

    if (result.error) {
      console.error('[Layers] Collection error', result.error.message);
      return;
    }

    const products = result.data.products || [];
    const totalResults = getTotalResults(result.data, products.length);
    const resolvedCount = Number.isFinite(Number(totalResults))
      ? Number(totalResults)
      : products.length;
    const hasResults = resolvedCount > 0;
    const hasActiveFilters = hasActiveFilterParams();

    setNoResultsState(section, hasResults);
    updateClearFiltersButton(section, hasActiveFilters);

    if (!hasResults && !append) {
      this.teardownCarousel();
      this.grid.innerHTML = '';
      const noResultsNode = buildNoResultsNode({
        message: 'No results found.',
        clearLabel: getClearFiltersLabel(section),
        showClear: hasActiveFilters,
      });
      this.grid.appendChild(noResultsNode);
    } else {
      const cards = products.map((product, index) =>
        renderProductCard(product, {
          columns: this.columns,
          columnsMobile: this.columnsMobile,
          index: append ? (this.grid.children.length + index) : index,
          aosDelay: index,
          aosAnchor: this.sectionAnchor,
          carousel: this.carousel,
        })
      );
      const combined = page === 1 ? injectPromos(cards, this.promos) : cards;
      if (append) {
        this.grid.insertAdjacentHTML('beforeend', combined.join(''));
      } else {
        this.teardownCarousel();
        this.grid.innerHTML = combined.join('');
      }
      this.refreshCarousel();
    }

    const totalPages = hasResults ? getTotalPages(result.data, this.limit, page) : 1;
    this.page = result.data?.page || page;
    this.totalPages = totalPages;
    renderPagination(this.paginationContainer, {
      currentPage: this.page,
      totalPages: this.totalPages,
    });

    setResultsCount(section, resolvedCount);
    const shouldUpdateFilters =
      page === 1 || !section?.querySelector(COLLECTION_UI.filtersList)?.children.length;
    if (shouldUpdateFilters) {
      const incomingFacets = result.data.facets || {};
      const incomingRanges = result.data.facetRanges || {};
      if (!hasActiveFilters) {
        this.facetSnapshot = incomingFacets;
        this.facetRangeSnapshot = incomingRanges;
      } else {
        this.facetSnapshot = this.facetSnapshot
          ? mergeFacetBuckets(this.facetSnapshot, incomingFacets)
          : incomingFacets;
        this.facetRangeSnapshot = this.facetRangeSnapshot
          ? mergeFacetRanges(this.facetRangeSnapshot, incomingRanges)
          : incomingRanges;
      }
      const facetsForUi = hasActiveFilters ? (this.facetSnapshot || incomingFacets) : incomingFacets;
      const rangesForUi = hasActiveFilters ? (this.facetRangeSnapshot || incomingRanges) : incomingRanges;
      updateSortOptions(section, ['collection', 'browse']);
      updateFiltersUI(section, facetsForUi, rangesForUi);
    }
  }

  async refresh() {
    if (this.isLoading) return;
    this.isLoading = true;
    this.page = getPageFromUrl();
    this.totalPages = 1;
    await this.fetchPage(this.page, { append: false });
    this.isLoading = false;
  }
}

class LayersSearchResults extends HTMLElement {
  connectedCallback() {
    this.grid = this.querySelector('[data-layers-grid]');
    if (!this.grid) return;
    const section = getSectionContainer(this);
    this.limit = Number(this.dataset.limit) || DEFAULT_PAGE_SIZE;
    this.columns = Number(this.dataset.columns) || 4;
    this.columnsMobile = Number(this.dataset.columnsMobile) || 2;
    this.sectionAnchor = this.grid.id ? `#${this.grid.id}` : '';
    this.isLoading = false;
    this.page = getPageFromUrl();
    this.totalPages = 1;
    this.paginationContainer = getPaginationContainer(this);
    this.skeletonMarkup = this.grid.innerHTML;
    this.hasSkeleton = this.skeletonMarkup.trim().length > 0;
    this.filtersList = section?.querySelector(COLLECTION_UI.filtersList) || null;
    this.filtersSkeletonMarkup = this.filtersList?.innerHTML || '';
    this.hasFiltersSkeleton = this.filtersSkeletonMarkup.trim().length > 0;
    this.facetSnapshot = null;
    this.facetRangeSnapshot = null;
    this.bindListeners();
    this.blankFiltersAndSort();
    updateSortOptions(section, ['search', 'text_search']);
    this.refresh();
  }

  scrollToGridTop() {
    if (!this.grid) return;
    const offset = Number(window.stickyHeaderHeight) || 0;
    const top = this.grid.getBoundingClientRect().top + window.pageYOffset - offset - 16;
    window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
  }

  showSkeleton() {
    if (!this.grid) return;
    if (!this.hasSkeleton) {
      this.grid.innerHTML = '';
      return;
    }
    this.grid.innerHTML = this.skeletonMarkup;
  }

  interceptFiltersForm(form) {
    if (!form || form.dataset.layersIntercepted === 'true') return;
    this.onFiltersInputCapture = (event) => {
      event.stopImmediatePropagation();
    };
    this.onFiltersSubmitCapture = (event) => {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.updateUrlFromFilters(form);
      setTimeout(this.refresh.bind(this), 0);
    };
    form.addEventListener('input', this.onFiltersInputCapture, true);
    form.addEventListener('submit', this.onFiltersSubmitCapture, true);
    form.dataset.layersIntercepted = 'true';
  }

  bindListeners() {
    this.onUrlChange = () => this.refresh();
    window.addEventListener('popstate', this.onUrlChange);

    const searchForm = document.querySelector(SEARCH_UI.searchForm);
    if (searchForm) {
      searchForm.addEventListener('submit', (event) => {
        event.preventDefault();
        event.stopImmediatePropagation();
        const formData = new FormData(searchForm);
        const query = formData.get('q')?.toString() || '';
        this.updateUrlFromSearch(query, getSearchIdFromUrl());
        setTimeout(this.refresh.bind(this), 50);
      });
    }

    const filtersForm = document.querySelector(SEARCH_UI.filtersForm);
    if (filtersForm) {
      this.filtersForm = filtersForm;
      this.interceptFiltersForm(filtersForm);
      filtersForm.addEventListener('change', (event) => {
        if (isSortInput(event.target)) {
          event.preventDefault();
          event.stopImmediatePropagation();
          const sortValue = getSortInputValue(event.target);
          if (sortValue) {
            this.updateUrlFromSort(sortValue);
            setTimeout(this.refresh.bind(this), 50);
          }
          return;
        }
        event.preventDefault();
        event.stopImmediatePropagation();
        this.updateUrlFromFilters(filtersForm);
        setTimeout(this.refresh.bind(this), 50);
      });
      filtersForm.addEventListener('theme:filter:range-update', (event) => {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.updateUrlFromFilters(filtersForm);
        setTimeout(this.refresh.bind(this), 50);
      });
    }
    const section = getSectionContainer(this);
    this.filtersResetButtons = Array.from(
      section?.querySelectorAll('.collection__filters__reset') || []
    );
    if (this.filtersResetButtons.length) {
      this.onFiltersResetClick = (event) => {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.clearFilters();
      };
      this.filtersResetButtons.forEach((button) => {
        button.addEventListener('click', this.onFiltersResetClick, { capture: true });
      });
    }
    this.onNoResultsClearClick = (event) => {
      const button = event.target.closest('[data-layers-clear-filters]');
      if (!button) return;
      if (button.hasAttribute('disabled') || button.getAttribute('aria-disabled') === 'true') return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.clearFilters();
    };
    this.grid?.addEventListener('click', this.onNoResultsClearClick);

    section?.addEventListener('change', (event) => {
      if (!isSortInput(event.target)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      const sortValue = getSortInputValue(event.target);
      if (sortValue) {
        this.updateUrlFromSort(sortValue);
        setTimeout(this.refresh.bind(this), 50);
      }
    });

    this.paginationContainer?.addEventListener('click', (event) => {
      const link = event.target.closest('a[data-page]');
      if (!link) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      const page = Number(link.dataset.page);
      if (!Number.isFinite(page) || page < 1 || page === this.page) return;
      this.updateUrlFromPage(page);
      setTimeout(() => {
        this.fetchPage(page, { append: false }).then(() => this.scrollToGridTop());
      }, 50);
    });
  }

  disconnectedCallback() {
    window.removeEventListener('popstate', this.onUrlChange);
    if (this.filtersForm && this.onFiltersInputCapture) {
      this.filtersForm.removeEventListener('input', this.onFiltersInputCapture, true);
    }
    if (this.filtersForm && this.onFiltersSubmitCapture) {
      this.filtersForm.removeEventListener('submit', this.onFiltersSubmitCapture, true);
    }
    if (this.filtersResetButtons && this.onFiltersResetClick) {
      this.filtersResetButtons.forEach((button) => {
        button.removeEventListener('click', this.onFiltersResetClick, { capture: true });
      });
    }
    if (this.grid && this.onNoResultsClearClick) {
      this.grid.removeEventListener('click', this.onNoResultsClearClick);
    }
  }

  clearFilters() {
    const params = getUrlParams();
    let changed = false;
    [...params.keys()].forEach((key) => {
      if (key.startsWith('filters[')) {
        params.delete(key);
        changed = true;
      }
    });
    params.delete('page');
    if (changed) {
      history.pushState({ layers: true }, '', `${window.location.pathname}?${params.toString()}`);
    }
    setTimeout(this.refresh.bind(this), 0);
  }

  blankFiltersAndSort() {
    const container = getSectionContainer(this);
    if (!container) return;
    const lists = container.querySelectorAll(`${COLLECTION_UI.sortOptionsList}, ${COLLECTION_UI.sortBarOptionsList}, ${COLLECTION_UI.filtersList}`);
    lists.forEach((list) => {
      if (list.matches(COLLECTION_UI.filtersList) && this.hasFiltersSkeleton) {
        list.innerHTML = this.filtersSkeletonMarkup;
        return;
      }
      list.innerHTML = '';
    });
  }

  updateUrlFromSearch(query, searchId) {
    const params = getUrlParams();
    params.set('q', query);
    params.set('type', 'product');
    if (searchId) params.set('search_id', searchId);
    params.delete('page');
    history.pushState({ layers: true }, '', `${window.location.pathname}?${params.toString()}`);
  }

  updateUrlFromSort(sortCode) {
    const params = getUrlParams();
    params.set('sort', sortCode);
    params.delete('page');
    history.pushState({ layers: true }, '', `${window.location.pathname}?${params.toString()}`);
  }

  updateUrlFromFilters(form) {
    const params = getUrlParams();
    [...params.keys()].forEach((key) => {
      if (key.startsWith('filters[')) params.delete(key);
    });
    const inputs = collectFilterInputs(form);
    inputs.forEach((input) => {
      if (!input || !input.name || input.disabled) return;
      const key = input.name;
      if (!key.startsWith('filters[')) return;
      if (input.type === 'checkbox' || input.type === 'radio') {
        if (!input.checked) return;
      }
      let value = String(input.value ?? '').trim();
      value = stripWrappingQuotes(value);
      if (value === '') return;
      params.append(key, value);
    });
    params.delete('page');
    history.pushState({ layers: true }, '', `${window.location.pathname}?${params.toString()}`);
  }

  updateUrlFromPage(page) {
    const params = getUrlParams();
    if (Number(page) > 1) {
      params.set('page', String(page));
    } else {
      params.delete('page');
    }
    history.pushState({ layers: true }, '', `${window.location.pathname}?${params.toString()}`);
  }

  async fetchPage(page, { append = false } = {}) {
    const sdk = getSdk();
    const query = getSearchQueryFromUrl();
    if (!sdk || !query) return;
    if (!append) this.showSkeleton();
    const section = getSectionContainer(this);

    const searchId = getSearchIdFromUrl();
    const config = getLayersConfig();
    const baseSorts = normalizeSorts(config.sortOrders || config.sorts || []);
    const searchSorts = filterSortsByScope(baseSorts, ['search', 'text_search']);
    const controller = sdk.search();
    const filters = buildFiltersFromUrl();
    const sortSelection = getSortSelection();
    const sortAllowed =
      !sortSelection ||
      !searchSorts.length ||
      searchSorts.some((item) => item.code === sortSelection.code);
    if (!sortAllowed && sortSelection) {
      console.warn('[Layers] Ignoring sort order outside search scope.');
    }
    const sortOrderCode = sortAllowed
      ? sortSelection?.code || getLayersConfig().defaultSortOrder
      : getLayersConfig().defaultSortOrder;

    const result = await controller.execute({
      query,
      searchId,
      page,
      limit: this.limit,
      filters,
      params: sortOrderCode ? { sortOrderCode } : undefined,
      includeFacetRanges: true,
    });

    if (window?.theme?.settings?.layersDebug) {
      console.log('[Layers][Search] request', {
        query,
        searchId,
        page,
        limit: this.limit,
        filters,
        sortOrderCode,
      });
    }

    if (result.error) {
      console.error('[Layers] Search error', result.error.message);
      return;
    }

    const products = result.data.products || [];
    const totalResults = getTotalResults(result.data, products.length);
    const resolvedCount = Number.isFinite(Number(totalResults))
      ? Number(totalResults)
      : products.length;
    const hasResults = resolvedCount > 0;
    const hasActiveFilters = hasActiveFilterParams();

    setNoResultsState(section, hasResults);
    updateClearFiltersButton(section, hasActiveFilters);

    if (!hasResults && !append) {
      this.grid.innerHTML = '';
      const queryLabel = getSearchQueryFromUrl();
      const message = queryLabel
        ? `No results found for "${queryLabel}".`
        : 'No results found.';
      const noResultsNode = buildNoResultsNode({
        message,
        clearLabel: getClearFiltersLabel(section),
        showClear: hasActiveFilters,
      });
      this.grid.appendChild(noResultsNode);
    } else {
      const cards = products.map((product, index) =>
        renderProductCard(product, {
          columns: this.columns,
          columnsMobile: this.columnsMobile,
          index: append ? (this.grid.children.length + index) : index,
          aosDelay: index,
          aosAnchor: this.sectionAnchor,
        })
      );
      if (append) {
        this.grid.insertAdjacentHTML('beforeend', cards.join(''));
      } else {
        this.grid.innerHTML = cards.join('');
      }
    }

    const totalPages = hasResults ? getTotalPages(result.data, this.limit, page) : 1;
    this.page = result.data?.page || page;
    this.totalPages = totalPages;
    renderPagination(this.paginationContainer, {
      currentPage: this.page,
      totalPages: this.totalPages,
    });

    setResultsCount(document.querySelector(SEARCH_UI.resultsWrapper) || section, resolvedCount);
    updateSearchTitle(section, resolvedCount);
    const shouldUpdateFilters =
      page === 1 || !section?.querySelector(COLLECTION_UI.filtersList)?.children.length;
    if (shouldUpdateFilters) {
      const incomingFacets = result.data.facets || {};
      const incomingRanges = result.data.facetRanges || {};
      if (!hasActiveFilters) {
        this.facetSnapshot = incomingFacets;
        this.facetRangeSnapshot = incomingRanges;
      } else {
        this.facetSnapshot = this.facetSnapshot
          ? mergeFacetBuckets(this.facetSnapshot, incomingFacets)
          : incomingFacets;
        this.facetRangeSnapshot = this.facetRangeSnapshot
          ? mergeFacetRanges(this.facetRangeSnapshot, incomingRanges)
          : incomingRanges;
      }
      const facetsForUi = hasActiveFilters ? (this.facetSnapshot || incomingFacets) : incomingFacets;
      const rangesForUi = hasActiveFilters ? (this.facetRangeSnapshot || incomingRanges) : incomingRanges;
      updateSortOptions(section, ['search', 'text_search']);
      updateFiltersUI(section, facetsForUi, rangesForUi);
    }
  }

  async refresh() {
    if (this.isLoading) return;
    this.isLoading = true;
    this.page = getPageFromUrl();
    this.totalPages = 1;
    await this.fetchPage(this.page, { append: false });
    this.isLoading = false;
  }

}

class LayersRecommendations extends HTMLElement {
  connectedCallback() {
    this.grid = this.querySelector('[data-layers-grid]');
    if (!this.grid) return;
    this.blockId = normalizeId(this.dataset.blockId);
    this.anchorId = normalizeId(this.dataset.anchorId);
    this.anchorHandle = this.dataset.anchorHandle;
    this.limit = Number(this.dataset.limit) || 12;
    this.columns = Number(this.dataset.columns) || 4;
    this.columnsMobile = Number(this.dataset.columnsMobile) || 2;
    this.sectionAnchor = this.grid.id ? `#${this.grid.id}` : '';
    this.skeletonMarkup = this.grid.innerHTML;
    this.hasSkeleton = this.skeletonMarkup.trim().length > 0;
    this.carousel = this.dataset.carousel === 'true' || this.dataset.carousel === '1';
    this.lazyLoad = this.dataset.lazyLoad === 'true' || this.dataset.lazyLoad === '1';
    this.lazyRootMargin = this.dataset.lazyRootMargin || '200px';
    if (this.lazyLoad) {
      this.setupLazyLoad();
    } else {
      this.refresh();
    }
  }

  showSkeleton() {
    if (!this.grid) return;
    this.teardownCarousel();
    if (!this.hasSkeleton) {
      this.grid.innerHTML = '';
      return;
    }
    this.grid.innerHTML = this.skeletonMarkup;
  }

  teardownCarousel() {
    if (!this.carousel || !this.grid) return;
    const Flickity = window.themeVendor?.Flickity || window.Flickity;
    const flkty = Flickity?.data ? Flickity.data(this.grid) : null;
    if (flkty) {
      flkty.destroy();
    }
    this.grid.classList.remove('flickity-enabled');
    const viewport = this.grid.querySelector('.flickity-viewport');
    if (viewport) {
      const slider = viewport.querySelector('.flickity-slider');
      if (slider) {
        while (slider.firstChild) {
          this.grid.appendChild(slider.firstChild);
        }
      }
      viewport.remove();
    }
    this.grid.style.removeProperty('height');
  }

  refreshCarousel() {
    if (!this.carousel || !this.grid) return;
    requestAnimationFrame(() => {
      if (this.grid.hasAttribute('data-slider')) {
        this.grid.dispatchEvent(new CustomEvent('theme:slider:resize', { bubbles: true }));
      }
      document.dispatchEvent(new CustomEvent('theme:resize:width', { bubbles: true }));
    });
  }

  setupLazyLoad() {
    if (this.lazyObserver) return;
    if (!('IntersectionObserver' in window)) {
      this.refresh();
      return;
    }
    this.lazyObserver = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.lazyObserver.disconnect();
        this.lazyObserver = null;
        this.refresh();
      },
      { rootMargin: this.lazyRootMargin, threshold: 0.01 }
    );
    this.lazyObserver.observe(this);
  }

  disconnectedCallback() {
    this.lazyObserver?.disconnect();
  }

  async refresh() {
    const sdk = getSdk();
    if (!sdk || !this.blockId) return;
    this.showSkeleton();

    const controller = sdk.blocks({
      blockId: this.blockId,
      anchorId: this.anchorId || undefined,
      anchorHandle: this.anchorHandle || undefined,
    });

    const result = await controller.execute({ limit: this.limit });
    if (result.error) {
      console.error('[Layers] Blocks error', result.error.message);
      return;
    }

    const products = result.data.products || [];
    const cards = products.map((product, index) =>
      renderProductCard(product, {
        columns: this.columns,
        columnsMobile: this.columnsMobile,
        index,
        aosDelay: index,
        aosAnchor: this.sectionAnchor,
        carousel: this.carousel,
      })
    );
    this.teardownCarousel();
    this.grid.innerHTML = cards.join('');
    this.refreshCarousel();
  }
}

function renderPredictiveMarkup({ queries, query, searchId }) {
  const safeQuery = query || '';
  const searchIdParam = searchId || '';
  const queryItems = queries
    .map(
      (item, index) => html`
        <div class="predictive-search__item" id="predictiveSearchOptionQuery-${index + 1}" role="option" aria-selected="false">
          <a class="predictive-search__link" href="${buildSearchUrl(item, searchIdParam)}" data-search-query="${encodeURIComponent(item)}" tabindex="-1">${item}</a>
        </div>`
    )
    .join('');

  return html`
    <div id="predictiveSearchResults" class="predictive-search__results">
      ${queries.length ? html`
        <div class="predictive-search__group" role="group" aria-labelledby="predictiveSearchSuggestions">
          <p id="predictiveSearchSuggestions" class="predictive-search__heading label-typography">Suggestions</p>
          <div class="predictive-search__list" role="listbox">
            ${queryItems}
          </div>
        </div>` : ''}
      <div class="predictive-search__actions" role="group" aria-labelledby="predictiveSearchActions">
        <p id="predictiveSearchActions" class="visually-hidden">Search actions</p>
        <a class="predictive-search__link" tabindex="-1" id="predictiveSearchActionViewAllResults" role="option" aria-selected="false" href="${buildSearchUrl(safeQuery, searchId)}">View all results</a>
      </div>
      <span class="hidden" data-predictive-search-live-region-count-value aria-live="polite">${queries.length} suggestions</span>
    </div>
  `;
}

function getSuggestionText(item) {
  if (item == null) return '';
  if (typeof item === 'string') return item;
  if (typeof item === 'number') return String(item);
  if (typeof item === 'object') {
    return (
      item.query_text ||
      item.queryText ||
      item.text ||
      item.query ||
      item.term ||
      item.value ||
      ''
    );
  }
  return '';
}

function normalizeAutocompleteQueries(data) {
  if (!data) return [];
  let raw = [];
  if (Array.isArray(data.matchedQueries)) raw = data.matchedQueries;
  else if (Array.isArray(data.matched_queries)) raw = data.matched_queries;
  else if (Array.isArray(data.queries)) raw = data.queries;
  else if (Array.isArray(data.suggestions)) raw = data.suggestions;
  return raw
    .map((item) => getSuggestionText(item).trim())
    .filter(Boolean);
}

function updatePredictiveLinks(container, searchId) {
  if (!container) return;
  const links = container.querySelectorAll('.predictive-search__link[data-search-query]');
  links.forEach((link) => {
    const raw = link.getAttribute('data-search-query') || '';
    let query = raw;
    try {
      query = decodeURIComponent(raw);
    } catch (error) {
      query = raw;
    }
    link.setAttribute('href', buildSearchUrl(query, searchId));
  });
  const viewAll = container.querySelector('#predictiveSearchActionViewAllResults');
  if (viewAll) {
    const baseQuery = viewAll.getAttribute('data-search-query');
    if (baseQuery) {
      let query = baseQuery;
      try {
        query = decodeURIComponent(baseQuery);
      } catch (error) {
        query = baseQuery;
      }
      viewAll.setAttribute('href', buildSearchUrl(query, searchId));
    }
  }
}

function ensureSearchIdInput(form) {
  if (!form) return null;
  let input = form.querySelector('input[name="search_id"]');
  if (!input) {
    input = document.createElement('input');
    input.type = 'hidden';
    input.name = 'search_id';
    form.appendChild(input);
  }
  return input;
}

function getSearchInputValue(form) {
  if (!form) return '';
  const input = form.querySelector('input[type="search"][name="q"]') || form.querySelector('input[name="q"]');
  return input?.value?.toString().trim() || '';
}

function bindPredictiveForm(predictiveEl) {
  if (!predictiveEl || predictiveEl._layersFormBound) return;
  const form = predictiveEl.querySelector('form[data-search-form]') || predictiveEl.querySelector('form.search-form');
  if (!form) return;
  predictiveEl._layersFormBound = true;
  predictiveEl._layersForm = form;
  form.addEventListener('submit', () => {
    const current = getSearchInputValue(form);
    const matches = current && predictiveEl._layersPreparedTerm === current;
    const searchId = matches ? predictiveEl._layersPreparedId : '';
    const field = ensureSearchIdInput(form);
    if (field) field.value = searchId || '';
  });
}

async function waitForAutocompleteState(controller, expectedQuery, shouldContinue, timeoutMs = 2000) {
  const start = Date.now();
  const previousState = controller.state?.value || null;
  const expected = expectedQuery ? String(expectedQuery).toLowerCase() : '';
  let hasStarted = false;

  while (Date.now() - start < timeoutMs) {
    if (typeof shouldContinue === 'function' && !shouldContinue()) return null;
    const state = controller.state?.value || null;
    if (state?.isFetching) hasStarted = true;
    const dataQuery = state?.data?.normalizedQuery || state?.data?.originalQuery || '';
    const matches = expected && dataQuery && String(dataQuery).toLowerCase() === expected;
    const stateChanged = state && state !== previousState;
    if (state && !state.isFetching && (matches || (hasStarted && stateChanged))) return state;
    await new Promise((resolve) => setTimeout(resolve, 50));
  }
  return controller.state?.value || null;
}

function patchPredictiveSearch() {
  const Predictive = customElements.get('predictive-search');
  if (!Predictive || Predictive.prototype._layersPatched) return false;
  Predictive.prototype._layersPatched = true;

  Predictive.prototype.getSearchResults = async function (term) {
    const sdk = getSdk();
    if (!sdk) return;

    const rawTerm = term == null ? '' : String(term);
    const trimmedTerm = rawTerm.trim();

    bindPredictiveForm(this);
    this.setLiveRegionLoadingState();
    const autocomplete = this._layersAutocomplete || sdk.autocomplete({ debounceMs: 0 });
    this._layersAutocomplete = autocomplete;
    const searchController = this._layersSearchController || sdk.search();
    this._layersSearchController = searchController;
    const requestId = (this._layersPredictiveRequestId || 0) + 1;
    this._layersPredictiveRequestId = requestId;
    const cachedSearchId =
      trimmedTerm && this._layersPreparedTerm === trimmedTerm ? this._layersPreparedId || '' : '';
    const shouldPrepare = trimmedTerm && !cachedSearchId;
    const preparePromise = shouldPrepare
      ? searchController.prepare({ query: trimmedTerm })
      : null;

    try {
      autocomplete.execute(trimmedTerm);
      const autocompleteState = await waitForAutocompleteState(
        autocomplete,
        trimmedTerm,
        () => this._layersPredictiveRequestId === requestId
      );
      if (this._layersPredictiveRequestId !== requestId) return;
      if (autocompleteState?.error) {
        console.error('[Layers] Autocomplete error', autocompleteState.error);
        this.close();
        return;
      }
      const queries = normalizeAutocompleteQueries(autocompleteState?.data);
      const markup = renderPredictiveMarkup({ queries, query: trimmedTerm, searchId: cachedSearchId });

      this.predictiveSearchResults.innerHTML = markup;
      const viewAllLink = this.predictiveSearchResults.querySelector('#predictiveSearchActionViewAllResults');
      if (viewAllLink) {
        viewAllLink.setAttribute('data-search-query', encodeURIComponent(trimmedTerm));
      }
      if (cachedSearchId) {
        updatePredictiveLinks(this.predictiveSearchResults, cachedSearchId);
        const formField = ensureSearchIdInput(this._layersForm);
        if (formField) formField.value = cachedSearchId;
      }
      this.setAttribute('results', true);
      this.setLiveRegionResults();
      this.open();
    } catch (error) {
      console.error('[Layers] Autocomplete error', error);
      this.close();
      return;
    }

    if (!preparePromise) return;

    try {
      const result = await preparePromise;
      if (this._layersPredictiveRequestId !== requestId) return;
      if (result?.error) {
        console.error('[Layers] Prepare search error', result.error);
        return;
      }
      const searchId = result?.data?.searchId;
      if (!searchId) return;
      this._layersPreparedTerm = trimmedTerm;
      this._layersPreparedId = searchId;

      updatePredictiveLinks(this.predictiveSearchResults, searchId);
      const formField = ensureSearchIdInput(this._layersForm);
      if (formField) formField.value = searchId;
    } catch (error) {
      if (this._layersPredictiveRequestId !== requestId) return;
      console.error('[Layers] Prepare search error', error);
    }
  };

  return true;
}

customElements.define('layers-collection-results', LayersCollectionResults);
customElements.define('layers-search-results', LayersSearchResults);
customElements.define('layers-recommendations', LayersRecommendations);

function ensurePredictiveSearchPatched() {
  if (patchPredictiveSearch()) return;
  if (!customElements.get('predictive-search')) {
    customElements.whenDefined('predictive-search').then(() => {
      patchPredictiveSearch();
    });
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', ensurePredictiveSearchPatched);
} else {
  ensurePredictiveSearchPatched();
}

const FILTER_DRAWER_SELECTOR = '[data-collection-filters]';
const FILTER_TRIGGER_SELECTOR = '[data-collapsible-trigger]';
const SHOW_MORE_TRIGGER_SELECTOR = '[data-show-more-trigger]';
const MOBILE_BREAKPOINT = 768;

function normalizeFacetText(value) {
  return String(value || '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

function isColorFacet(label, key) {
  const normalizedLabel = normalizeFacetText(label);
  const normalizedKey = normalizeFacetText(key);
  return (
    normalizedLabel === 'color' ||
    normalizedLabel === 'colour' ||
    normalizedLabel.includes('color') ||
    normalizedLabel.includes('colour') ||
    normalizedKey === 'color' ||
    normalizedKey.endsWith('.color') ||
    normalizedKey.endsWith('_color') ||
    normalizedKey.includes('color')
  );
}

function applyFilterAccordionState(container) {
  if (!container) return;
  const triggers = container.querySelectorAll(
    `${FILTER_TRIGGER_SELECTOR}:not(${SHOW_MORE_TRIGGER_SELECTOR})`
  );
  if (!triggers.length) return;

  triggers.forEach((trigger) => {
    const label = trigger.getAttribute('data-filter-label') || '';
    const fallback = trigger.textContent || '';
    const key = trigger.getAttribute('data-filter-key') || '';
    const filterContainer = trigger.closest('.collection__filter');
    const hasSwatches = Boolean(filterContainer?.querySelector('.collection__filter-tags--swatches'));
    const shouldKeepOpen = isSortAccordionTrigger(trigger) && isNonDefaultSortApplied(container)
      ? true
      : resolveAccordionState(container, {
        key,
        label,
        controls: trigger.getAttribute('aria-controls'),
        fallback,
        hasSwatches,
      });
    setCollapsibleState(trigger, shouldKeepOpen);
  });
}

function applyFilterAccordions() {
  document
    .querySelectorAll(FILTER_DRAWER_SELECTOR)
    .forEach((drawer) => applyFilterAccordionState(drawer));
}

function scheduleFilterAccordionUpdate() {
  window.requestAnimationFrame(() => {
    applyFilterAccordions();
  });
}

function initFilterAccordions() {
  scheduleFilterAccordionUpdate();
  document.addEventListener('theme:filters:init', () => {
    window.setTimeout(scheduleFilterAccordionUpdate, 0);
    document.querySelectorAll(FILTER_DRAWER_SELECTOR).forEach((drawer) => updateFilterLabels(drawer));
  });
  document.addEventListener('theme:resize:width', () => {
    scheduleFilterAccordionUpdate();
    document.querySelectorAll(FILTER_DRAWER_SELECTOR).forEach((drawer) => updateFilterLabels(drawer));
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initFilterAccordions);
} else {
  initFilterAccordions();
}
